/* Modal público para mostrar la disponibilidad de una zona
No permite vender, seleccionar ni nada similar, solo ver. */
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, Box, Alert, IconButton, CircularProgress, Paper, useMediaQuery, useTheme, Chip } from '@mui/material';
import { Close, ZoomIn, ZoomOut, RestartAlt, Refresh, } from '@mui/icons-material';
import { Stage, Layer, Circle, Text, Group } from 'react-konva';
import { api } from '../../../services/api';

const PublicDisponibilidadModal = ({ open, onClose, zona }) => {
  const [layoutData, setLayoutData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [escala, setEscala] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });

  const stageRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const CANVAS_WIDTH = isMobile ? window.innerWidth - 20 : 800;
  const CANVAS_HEIGHT = isMobile ? window.innerHeight - 20 : 600;

  useEffect(() => {
    if (open && zona) {
      cargarLayoutZona();
    }
  }, [open, zona]);

  useEffect(() => {
    if (!open) {
      setLayoutData(null);
      setError(null);
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  }, [open]);

  const cargarLayoutZona = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.ubicacionesNew.getLayoutZona(zona.id);
      if (response.status === "success") {
        setLayoutData(response.data);
      } else {
        setError("No se pudo cargar el mapa de la zona");
      }
    } catch (error) {
      console.error("Error cargando layout:", error);
      setError("Error de conexión al servidor");
    } finally {
      setLoading(false);
    }
  };

  const zoomIn = () => {
    const newScale = Math.min(escala * 1.2, 3);
    setEscala(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(escala * 0.8, 0.3);
    setEscala(newScale);
  };

  const resetZoom = () => {
    setEscala(1);
    setPosicion({ x: 0, y: 0 });
  };

  const handleDragCanvas = (e) => {
    if (e.target === e.target.getStage()) {
      setPosicion({ x: e.target.x(), y: e.target.y() });
    }
  };

  const AsientoCanvas = ({ asiento, esDeMesa, mesa, index }) => {
    const estaOcupado = asiento.ocupado;

    let posX = asiento.coordX || asiento.coord_x || 0;
    let posY = asiento.coordY || asiento.coord_y || 0;

    if (esDeMesa && mesa && (posX === 0 && posY === 0)) {
      const centroMesaX = mesa.coordX + (mesa.ancho / 2);
      const centroMesaY = mesa.coordY + (mesa.alto / 2);
      const radioAsientos = Math.max(mesa.ancho, mesa.alto) / 2 + 25;

      const angulo = (2 * Math.PI * index) / mesa.asientos.length;
      posX = centroMesaX + radioAsientos * Math.cos(angulo);
      posY = centroMesaY + radioAsientos * Math.sin(angulo);
    }

    if (posX === 0 && posY === 0 && !esDeMesa) {
      const cols = 15;
      posX = (index % cols) * 40 + 20;
      posY = Math.floor(index / cols) * 40 + 20;
    }

    const radius = isMobile ? 12 : 15;

    return (
      <Group x={posX} y={posY}>
        <Circle
          x={0}
          y={0}
          radius={radius}
          fill={estaOcupado ? "#5C636A" : "#10b981"}
          stroke={estaOcupado ? "#5C636A" : "#059669"}
          strokeWidth={2}
          listening={false}
        />

        <Text
          x={0}
          y={0}
          text={esDeMesa ? asiento.asiento.replace(/^Mesa \d+-/, '') : asiento.asiento}
          fontSize={isMobile ? 8 : 10}
          fill="white"
          listening={false}
          offsetX={4}
          offsetY={4}
          fontStyle="bold"
        />
      </Group>
    );
  };

  const MesaCanvas = ({ mesa, index }) => {
    const centroX = mesa.coordX + mesa.ancho / 2;
    const centroY = mesa.coordY + mesa.alto / 2;
    const radius = Math.min(mesa.ancho, mesa.alto) / 2;
    const numeroMesa = mesa.mesa.replace(/^Mesa /, '');

    return (
      <Group key={mesa.id}>
        <Circle
          x={centroX}
          y={centroY}
          radius={radius}
          fill="#8BC34A"
          stroke="#689F38"
          strokeWidth={2}
          listening={false}
        />

        <Text
          x={centroX}
          y={centroY - (isMobile ? 6 : 8)}
          text="Mesa"
          fontSize={isMobile ? 10 : 12}
          fill="white"
          listening={false}
          offsetX={12}
          offsetY={0}
          align="center"
          fontStyle="bold"
        />

        <Text
          x={centroX}
          y={centroY + (isMobile ? 3 : 5)}
          text={numeroMesa}
          fontSize={isMobile ? 12 : 14}
          fill="white"
          listening={false}
          offsetX={6}
          offsetY={0}
          align="center"
          fontStyle="bold"
        />

        {mesa.asientos?.map((asiento, asientoIndex) => (
          <AsientoCanvas
            key={asiento.id}
            asiento={asiento}
            esDeMesa={true}
            mesa={mesa}
            index={asientoIndex}
          />
        ))}
      </Group>
    );
  };

  if (!zona) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            height: '85vh',
            maxHeight: isMobile ? '85vh' : '950px',
            borderRadius: isMobile ? '16px 16px 0 0' : '12px',
            boxShadow: '0 8px 32px rgba(44, 62, 80, 0.12)',
            ...(isMobile && {
              position: 'fixed',
              bottom: 0,
              top: 'auto',
              left: 0,
              right: 0,
              transform: 'none',
              margin: 0,
              width: '100%'
            })
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          fontWeight: 700,
          fontSize: isMobile ? '18px' : '20px',
          color: '#0b0060',
          borderBottom: '1px solid #E4E4E7',
          mb: isMobile ? 1 : 2,
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: '#0b0060',
                fontSize: isMobile ? '16px' : '18px'
              }}>
                Mapa de Disponibilidad - {zona.ubicacion}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Estado actual de asientos en tiempo real
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton onClick={cargarLayoutZona} size="small" title="Actualizar">
                <Refresh />
              </IconButton>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
          {isMobile && (
            <Box sx={{
              display: 'flex',
              gap: 1,
              mt: 2,
              justifyContent: 'start',
              flexWrap: 'wrap'
            }}>
              <Chip
                label="Disponible"
                size="small"
                sx={{
                  bgcolor: '#10b981',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '12px',
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
              <Chip
                label="Ocupado"
                size="small"
                sx={{
                  bgcolor: '#5C636A',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '12px',
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
              <Chip
                label="Mesa"
                size="small"
                sx={{
                  bgcolor: '#8BC34A',
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '12px',
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 3, position: 'relative', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
              <CircularProgress size={50} />
              <Typography sx={{ ml: 2 }}>Cargando mapa...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : layoutData ? (
            <Box sx={{
              display: 'flex',
              gap: isMobile ? 0 : 3,
              height: isMobile ? '100%' : 'auto'
            }}>
              {/* PANEL IZQ DESKTOP */}
              {!isMobile && (
                <Paper elevation={0} sx={{
                  width: 280,
                  bgcolor: '#F9FAFB',
                  borderRadius: '12px',
                  p: 3,
                  boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
                  border: '1px solid #E4E4E7',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  height: 'fit-content'
                }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#5656ff',
                    mb: 1
                  }}>
                    Leyenda
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 18, height: 18, bgcolor: '#10b981', borderRadius: '50%', border: '2px solid #059669' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Disponible
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 18, height: 18, bgcolor: '#5C636A', borderRadius: '50%', border: '2px solid #5C636A' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Ocupado
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 18, height: 18, bgcolor: '#8BC34A', borderRadius: '50%', border: '2px solid #689F38' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Mesa
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'white',
                    borderRadius: '8px',
                    p: 1,
                    border: '1px solid #e5e7eb'
                  }}>
                    <IconButton size="small" onClick={zoomOut} sx={{ width: 32, height: 32 }}>
                      <ZoomOut fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" sx={{ mx: 1, minWidth: '60px', textAlign: 'center', fontSize: '12px' }}>
                      {Math.round(escala * 100)}%
                    </Typography>
                    <IconButton size="small" onClick={zoomIn} sx={{ width: 32, height: 32 }}>
                      <ZoomIn fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={resetZoom} sx={{ ml: 0.5, width: 32, height: 32 }}>
                      <RestartAlt fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              )}

              <Box sx={{ flex: 1, position: 'relative' }}>
                <Paper elevation={0} sx={{
                  bgcolor: '#f9fafb',
                  borderRadius: isMobile ? '8px' : '12px',
                  border: '1px solid #e5e7eb',
                  width: CANVAS_WIDTH,
                  height: CANVAS_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  position: 'relative',
                  margin: isMobile ? '0 auto' : 0
                }}>
                  <Stage
                    width={CANVAS_WIDTH}
                    height={CANVAS_HEIGHT}
                    ref={stageRef}
                    scaleX={escala}
                    scaleY={escala}
                    x={posicion.x}
                    y={posicion.y}
                    draggable
                    onDragEnd={handleDragCanvas}
                    style={{
                      background: 'transparent',
                      cursor: escala > 1 ? 'grab' : 'default'
                    }}
                    onMouseDown={(e) => {
                      if (escala > 1) e.target.getStage().container().style.cursor = 'grabbing';
                    }}
                    onMouseUp={(e) => {
                      if (escala > 1) e.target.getStage().container().style.cursor = 'grab';
                    }}
                  >
                    <Layer>
                      {layoutData.ubicacion?.mesas?.map((mesa, index) => (
                        <MesaCanvas key={mesa.id} mesa={mesa} index={index} />
                      ))}

                      {layoutData.ubicacion?.asientos?.filter(asiento => !asiento.idmesa).map((asiento, index) => (
                        <AsientoCanvas
                          key={asiento.id}
                          asiento={asiento}
                          esDeMesa={false}
                          mesa={null}
                          index={index}
                        />
                      ))}
                    </Layer>
                  </Stage>

                  {(!layoutData.ubicacion?.mesas || layoutData.ubicacion.mesas.length === 0) &&
                    (!layoutData.ubicacion?.asientos || layoutData.ubicacion.asientos.filter(a => !a.idmesa).length === 0) && (
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: '#6b7280'
                      }}>
                        <Typography variant="h6">No hay asientos configurados</Typography>
                        <Typography variant="body2">
                          Esta zona no tiene un diseño configurado
                        </Typography>
                      </Box>
                    )}
                </Paper>

                {/* ZOOM FLOTANTE */}
                {isMobile && (
                  <>
                    <Box sx={{
                      position: 'absolute',
                      bottom: 20,
                      right: 20,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      p: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <IconButton size="medium" onClick={zoomIn} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                        <ZoomIn />
                      </IconButton>
                      <Typography variant="caption" sx={{ textAlign: 'center', px: 1, fontSize: '10px' }}>
                        {Math.round(escala * 100)}%
                      </Typography>
                      <IconButton size="medium" onClick={zoomOut} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                        <ZoomOut />
                      </IconButton>
                      <IconButton size="medium" onClick={resetZoom} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                        <RestartAlt />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ m: 2 }}>
              No se encontró información de layout para esta zona.
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicDisponibilidadModal;